/**
 * The design-D flow's icons: a few Lucide icons (ISC licence, public/licences.txt, credited on the Terms page), drawn as
 * inline SVG in `currentColor`. Decorative only: always aria-hidden, the words beside them carry the meaning. Icons that
 * point along the reading direction are mirrored in right-to-left languages (`.fd-flip`, flowD.css).
 */
export type IconNode = [string, Record<string, string>];

const FILE = 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z';
const FOLD = 'M14 2v4a2 2 0 0 0 2 2h4';

/** The start screen's icons (static: the start screen loads them); the flow's others are in ./stepIcons.ts (lazy). */
export const START_NODES = {
  'chevron-right': [['path', { d: 'm9 18 6-6-6-6' }]],
  'shield-check': [['path', { d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z' }], ['path', { d: 'm9 12 2 2 4-4' }]],
  receipt: [['path', { d: 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z' }], ['path', { d: 'M14 8H8' }], ['path', { d: 'M16 12H8' }], ['path', { d: 'M13 16H8' }]],
  'file-check': [['path', { d: FILE }], ['path', { d: FOLD }], ['path', { d: 'm9 15 2 2 4-4' }]],
  languages: [['path', { d: 'm5 8 6 6' }], ['path', { d: 'm4 14 6-6 2-3' }], ['path', { d: 'M2 5h12' }], ['path', { d: 'M7 2h1' }], ['path', { d: 'm22 22-5-10-5 10' }], ['path', { d: 'M14 18h6' }]],
  banknote: [['rect', { width: '20', height: '12', x: '2', y: '6', rx: '2' }], ['circle', { cx: '12', cy: '12', r: '2' }], ['path', { d: 'M6 12h.01M18 12h.01' }]],
} satisfies Record<string, IconNode[]>;

export type StartIcon = keyof typeof START_NODES;

/** The SVG namespace: a name, never fetched (in parts, so the no-network scan, src/smoke.test.ts, has nothing to match). */
export const SVG_NS = ['http:', '', 'www.w3.org', '2000', 'svg'].join('/');

/** A decorative inline SVG in `currentColor` from an icon's nodes; the caller's CSS sets size and colour. `flip`: mirrored in right-to-left. */
export function drawIcon(nodes: readonly IconNode[], flip: boolean, extraClass = ''): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg') as SVGElement;
  const set = (n: Element, a: Record<string, string>) => Object.entries(a).forEach(([k, v]) => n.setAttribute(k, v));
  set(svg, { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true', focusable: 'false' });
  svg.setAttribute('class', `fd-icon${flip ? ' fd-flip' : ''}${extraClass ? ` ${extraClass}` : ''}`);
  for (const [tag, a] of nodes) {
    const n = document.createElementNS(SVG_NS, tag);
    set(n, a);
    svg.append(n);
  }
  return svg;
}

/** A start-screen icon (the chevron points along the reading direction: mirrored in right-to-left). */
export const startIcon = (name: StartIcon, extraClass = ''): SVGElement => drawIcon(START_NODES[name], name === 'chevron-right', extraClass);
